import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inMarketTheme } from '@inmarket/inmarkit';
import { Button, Divider, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import ContractIcon from '../../assets/contract-icon.svg';
import useConfirm from '../../hooks/useConfirm';
import useModal from '../../hooks/useModal';
import ContractsList from '../Contracts/Contracts';
import { ContractListMode } from '../Contracts/ContractsTable';
import FormEndOptions from '../forms/FormEndOptions';

interface ContractsProps {
  options: any[];
  onChange: Function;
}
function Contracts({ options, onChange }: ContractsProps) {
  const [hoveredContractID, setHoverContractId] = useState();
  const [hoveredDeleteContractID, setHoverDeleteContractId] = useState();
  const [tempSelectedContracts, setTempSelectedContracts] = useState([]);
  const { windowDotConfirm: confirmRemoveContract } = useConfirm(
    `Are you sure you want to remove this contract from this report template?`
  );
  const { windowDotConfirm: confirmRemoveAllContracts } = useConfirm(
    `Are you sure you want to remove all contracts from this report template?`
  );

  const removeContract = async (contractId) => {
    if (await confirmRemoveContract()) {
      onChange(
        options.filter((contract) => contract.id !== contractId),
        true
      );
    }
  };

  const removeAllContracts = async () => {
    if (await confirmRemoveAllContracts()) {
      onChange([], true);
    }
  };

  useEffect(() => {
    setTempSelectedContracts(options);
  }, [options, setTempSelectedContracts]);

  const [openContractsModal, closeContractsModal] = useModal(
    () => {
      const addContracts = () => {
        onChange(tempSelectedContracts);
      };

      return (
        <Stack spacing={2}>
          <Stack pl={2}>
            <Typography variant="h5">Select Contracts</Typography>
          </Stack>
          <Stack sx={{ height: '500px', overflowY: 'scroll' }}>
            <ContractsList
              mode={ContractListMode.SelectOnly}
              value={tempSelectedContracts}
              onChange={setTempSelectedContracts}
            />
          </Stack>
          <FormEndOptions>
            <Stack direction="row" justifyContent="center" width="100%" spacing={1}>
              <Button onClick={closeContractsModal} variant="outlined">
                Close
              </Button>
              <Button
                onClick={() => {
                  addContracts();
                  closeContractsModal();
                }}
              >
                Add Selected Contracts
              </Button>
            </Stack>
          </FormEndOptions>
        </Stack>
      );
    },
    null,
    [onChange, tempSelectedContracts, setTempSelectedContracts]
  );

  return (
    <Stack justifyContent="space-between" height="100%">
      {options.length === 0 && (
        <Stack p={3} mt={4} alignItems="center" textAlign="center">
          <img alt="Contract Icon" src={ContractIcon} />
          <Typography variant="h6">No contracts have been added.</Typography>
          <Typography>Select contracts to add them to this report template.</Typography>
        </Stack>
      )}
      {options.length > 0 && (
        <Stack pb={10} onMouseOut={() => setHoverContractId(undefined)}>
          {options.map((item) => (
            <Stack key={item.id}>
              <Stack
                direction="row"
                p={2}
                spacing={2}
                justifyContent="space-between"
                onMouseOver={() => setHoverContractId(item.id)}
                onMouseOut={(e) => e.stopPropagation()}
                sx={{ bgcolor: hoveredContractID === item.id && 'rgba(45, 45, 45, 0.12)' }}
              >
                <Typography variant="body2">{item.name}</Typography>
                <Stack sx={{ width: '16px' }}>
                  {hoveredContractID === item.id && (
                    <Tooltip title="Delete Contract">
                      <FontAwesomeIcon
                        icon={faTrash}
                        onClick={() => removeContract(item.id)}
                        style={{ cursor: 'pointer' }}
                        fontSize="16px"
                        color={hoveredDeleteContractID === item.id ? inMarketTheme.palette.error.main : '#8C8C8C'}
                        onMouseOver={() => setHoverDeleteContractId(item.id)}
                        onMouseOut={() => setHoverDeleteContractId(undefined)}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
              <Divider />
            </Stack>
          ))}
        </Stack>
      )}
      <Stack p={1} className="reporting-hub-sticky-CTA">
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="text"
            startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: '18px', fontWeight: 900 }} />}
            onClick={openContractsModal}
          >
            Add Contracts
          </Button>
          <Button
            variant="text"
            startIcon={<FontAwesomeIcon icon={faTrash} style={{ fontSize: '18px', fontWeight: 900 }} />}
            onClick={removeAllContracts}
          >
            Remove All
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default Contracts;
